import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/shared/hooks/use-auth";

// ── Website module ──
const Index = lazy(() => import("@/modules/website/pages/Index"));
const Features = lazy(() => import("@/modules/website/pages/Features"));
const Pricing = lazy(() => import("@/modules/website/pages/Pricing"));
const About = lazy(() => import("@/modules/website/pages/About"));
const Contact = lazy(() => import("@/modules/website/pages/Contact"));
const HelpCenter = lazy(() => import("@/modules/website/pages/HelpCenter"));
const Partners = lazy(() => import("@/modules/website/pages/Partners"));
const ApiDocs = lazy(() => import("@/modules/website/pages/ApiDocs"));
const PrivacyPolicy = lazy(() => import("@/modules/website/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/modules/website/pages/TermsOfService"));
const CookiePolicy = lazy(() => import("@/modules/website/pages/CookiePolicy"));
const ArchitectureDoc = lazy(() => import("@/modules/website/pages/ArchitectureDoc"));
const SOC2ComplianceDoc = lazy(() => import("@/modules/website/pages/SOC2ComplianceDoc"));
const PMSIntegrationDoc = lazy(() => import("@/modules/website/pages/PMSIntegrationDoc"));
const NotFound = lazy(() => import("@/modules/website/pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 1000 * 60 * 5, retry: 1 },
  },
});

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="flex flex-col items-center gap-3">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      <span className="text-sm text-muted-foreground">Loading…</span>
    </div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Marketing website */}
              <Route path="/" element={<Index />} />
              <Route path="/features" element={<Features />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/help" element={<HelpCenter />} />
              <Route path="/partners" element={<Partners />} />
              <Route path="/api-docs" element={<ApiDocs />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              <Route path="/architecture" element={<ArchitectureDoc />} />
              <Route path="/soc2" element={<SOC2ComplianceDoc />} />
              <Route path="/pms-integrations" element={<PMSIntegrationDoc />} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
